import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  ClickAwayListener,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import countries_lib from 'i18n-iso-countries';
import countries_en from 'i18n-iso-countries/langs/en.json';
import countries_fr from 'i18n-iso-countries/langs/fr.json';
import countries_ar from 'i18n-iso-countries/langs/ar.json';

countries_lib.registerLocale(countries_en);
countries_lib.registerLocale(countries_fr);
countries_lib.registerLocale(countries_ar);

const REGION_LABELS = {
  north_africa: { en: 'North Africa', fr: 'Afrique du Nord', ar: 'شمال أفريقيا' },
  gcc: { en: 'GCC', fr: 'CCG', ar: 'مجلس التعاون الخليجي' },
  west_africa: { en: 'West Africa', fr: 'Afrique de l\'Ouest', ar: 'غرب أفريقيا' },
  east_africa: { en: 'East Africa', fr: 'Afrique de l\'Est', ar: 'شرق أفريقيا' },
  middle_east: { en: 'Middle East', fr: 'Moyen-Orient', ar: 'الشرق الأوسط' },
  europe: { en: 'Europe', fr: 'Europe', ar: 'أوروبا' },
};

function countryFlag(code) {
  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

const CountryRegionFilter = ({
  countries = [],
  regions = [],
  selectedCountries = [],
  selectedRegion = null,
  onCountriesChange,
  onRegionChange,
  detectedCountry = null,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const dropdownRef = useRef(null);

  const lang = i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en';

  const countryLabel = (code) =>
    countries_lib.getName(code, lang) || code;

  const regionLabel = (code) => {
    const labels = REGION_LABELS[code];
    if (labels) return labels[lang] || labels.en;
    const key = `news_region.${code}`;
    const val = t(key);
    return val === key ? code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : val;
  };

  // Sort countries: detected first, then alphabetical
  const sortedCountries = useMemo(() => {
    const filtered = countries.filter(
      (c) =>
        countryLabel(c).toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.toLowerCase().includes(countrySearch.toLowerCase())
    );
    if (detectedCountry) {
      const idx = filtered.indexOf(detectedCountry);
      if (idx > 0) {
        filtered.splice(idx, 1);
        filtered.unshift(detectedCountry);
      }
    }
    return filtered;
  }, [countries, countrySearch, detectedCountry, lang]);

  const toggleCountry = (code) => {
    const next = selectedCountries.includes(code)
      ? selectedCountries.filter((c) => c !== code)
      : [...selectedCountries, code];
    onCountriesChange(next);
  };

  const clearCountries = () => {
    onCountriesChange([]);
    setCountryDropdownOpen(false);
    setCountrySearch('');
  };

  const countryButtonLabel = () => {
    if (selectedCountries.length === 0) return t('news_hub.select_country');
    if (selectedCountries.length === 1)
      return `${countryFlag(selectedCountries[0])} ${countryLabel(selectedCountries[0])}`;
    return `${selectedCountries.map((c) => countryFlag(c)).join(' ')} ${selectedCountries.length} ${t('news_hub.countries_selected')}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Country filter */}
      {countries.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <PublicIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.disabled, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
              {t('news_hub.country_filter')}
            </Typography>
            {selectedCountries.length > 0 && (
              <Typography
                variant="caption"
                onClick={clearCountries}
                sx={{
                  ml: 'auto',
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t('news_hub.clear_filter')}
              </Typography>
            )}
          </Box>

          <ClickAwayListener onClickAway={() => { setCountryDropdownOpen(false); setCountrySearch(''); }}>
            <Box sx={{ position: 'relative' }} ref={dropdownRef}>
              <Box
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: selectedCountries.length > 0 ? theme.palette.text.primary : theme.palette.text.disabled,
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: theme.palette.text.secondary },
                }}
              >
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 0.5 }}>
                  {countryButtonLabel()}
                </Box>
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.disabled,
                    transition: 'transform 0.2s',
                    transform: countryDropdownOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </Box>

              {countryDropdownOpen && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    mt: 0.5,
                    zIndex: 20,
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[4],
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t('news_hub.search_country')}
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: '0.8rem', height: 32 },
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                    />
                  </Box>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', px: 0.5, pb: 0.5 }}>
                    {sortedCountries.length === 0 ? (
                      <Typography variant="caption" sx={{ px: 1.5, py: 1, display: 'block', color: theme.palette.text.disabled }}>
                        {t('news_hub.no_results')}
                      </Typography>
                    ) : (
                      sortedCountries.map((code) => {
                        const isSelected = selectedCountries.includes(code);
                        return (
                          <Box
                            key={code}
                            onClick={() => toggleCountry(code)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: isSelected ? 600 : 400,
                              color: isSelected ? theme.palette.text.primary : theme.palette.text.secondary,
                              backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                              '&:hover': {
                                backgroundColor: isSelected
                                  ? alpha(theme.palette.primary.main, 0.12)
                                  : theme.palette.action.hover,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                ...(isSelected
                                  ? { backgroundColor: theme.palette.primary.main }
                                  : { border: `1.5px solid ${theme.palette.divider}` }),
                              }}
                            >
                              {isSelected && <CheckIcon sx={{ fontSize: 14, color: theme.palette.primary.contrastText }} />}
                            </Box>
                            <span>{countryFlag(code)}</span>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {countryLabel(code)}
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        </Box>
      )}

      {/* Region filter */}
      {regions.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.disabled, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
              {t('news_hub.region_filter')}
            </Typography>
            {selectedRegion && (
              <Typography
                variant="caption"
                onClick={() => onRegionChange(null)}
                sx={{
                  ml: 'auto',
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t('news_hub.clear_filter')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {regions.map((region) => {
              const isSelected = selectedRegion === region;
              return (
                <Box
                  key={region}
                  onClick={() => onRegionChange(isSelected ? null : region)}
                  sx={{
                    px: 1.5,
                    py: 0.875,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.12)
                        : theme.palette.action.hover,
                      color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                    },
                  }}
                >
                  {regionLabel(region)}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CountryRegionFilter;
